/**
 * IXD 편집기 값 검증 — 공유 변환 모듈(`@/utils/value-conversion`)의 어댑터.
 *
 * 변환 로직 자체는 value-spec(05·06)의 단일 구현인 공유 모듈이 소유한다. 이 파일은
 * 편집기 경로의 컨텍스트 조립(schemaType→ValueKind, unitDivisor→scale, negated 판정)과
 * 사유코드→사용자 메시지 변환만 담당한다 (value-spec PLAN D4·P2-3).
 */

import {
  rawValue,
  resolveDecimalDigits,
  storedToView,
  stripInvisibleEdges,
  toStored,
  toView,
  trustedStoredValue,
  viewValue,
  type RejectReason,
  type ValueKind,
} from "@/utils/value-conversion";
import {
  displayTextToEditingText,
  insertThousandSeparators,
  toUnitScale,
} from "./unit-conversion";

type NumericDisplayMode = "raw" | "unit-applied";

interface NormalizeEditedCellValueOptions {
  schemaType?: string | null;
  inputValue: string;
  unitDivisor?: number;
  displayValueMode?: NumericDisplayMode;
  displayUsesNegatedSign?: boolean;
  negated?: boolean;
  /** 소수점 아래 자릿수 제약. null/undefined면 제약 없음. 초과분은 버림(절삭). */
  decimalDigits?: number | null;
}

interface ValidateStoredCellValueOptions {
  schemaType?: string | null;
  storedValue: string;
}

type ValidationFailure = {
  ok: false;
  errorMessage: string;
};

type EditedValueValidationSuccess = {
  ok: true;
  storedValue: string;
};

type StoredValueValidationSuccess = {
  ok: true;
  normalizedValue: string;
};

export type EditedValueValidationResult =
  | EditedValueValidationSuccess
  | ValidationFailure;

export type StoredValueValidationResult =
  | StoredValueValidationSuccess
  | ValidationFailure;

/**
 * schemaType → 공유 모듈 ValueKind (value-spec PLAN §2.1 front 열).
 *
 * 분기가 아니라 표로 두는 이유: 한 schemaType이 두 kind에 속하는 상태를 만들 수 없어야
 * 한다. 이전 구조에서 percentItemType이 percent·numeric 집합 양쪽에 있어 검사 순서가
 * 바뀌면 ÷100이 조용히 사라졌다 — 주석으로만 막던 함정이다. 객체 리터럴은 키 중복을
 * 컴파일 에러(TS1117)로 막으므로 그 실수 자체가 불가능해지고, 순서도 무의미해진다.
 *
 * TEXT(string·textBlock·escaped)는 넣지 않는다 — 미상 schemaType과 같은 폴백이라
 * 목록을 유지해도 판정이 달라지지 않는다. 모르는 타입을 숫자로 해석해 값을 바꾸지 않는
 * 것이 폴백의 목적이다.
 */
const SCHEMA_TYPE_VALUE_KINDS = new Map<string, ValueKind>(
  Object.entries({
    monetaryItemType: "MONETARY",
    percentItemType: "PERCENT",
    dateItemType: "DATE",
    durationItemType: "DURATION",
    booleanItemType: "BOOLEAN",

    decimalItemType: "NUMERIC",
    floatItemType: "NUMERIC",
    doubleItemType: "NUMERIC",
    integerItemType: "NUMERIC",
    nonNegativeIntegerItemType: "NUMERIC",
    positiveIntegerItemType: "NUMERIC",
    negativeIntegerItemType: "NUMERIC",
    nonPositiveIntegerItemType: "NUMERIC",
    longItemType: "NUMERIC",
    intItemType: "NUMERIC",
    shortItemType: "NUMERIC",
    byteItemType: "NUMERIC",
    unsignedLongItemType: "NUMERIC",
    unsignedIntItemType: "NUMERIC",
    unsignedShortItemType: "NUMERIC",
    unsignedByteItemType: "NUMERIC",
    sharesItemType: "NUMERIC",
    pureItemType: "NUMERIC",
    perShareItemType: "NUMERIC",
    fractionItemType: "NUMERIC",
  } satisfies Record<string, ValueKind>),
);

export const getIxdSchemaValueKind = (schemaType?: string | null): ValueKind =>
  SCHEMA_TYPE_VALUE_KINDS.get(schemaType?.trim() ?? "") ?? "TEXT";

// value-spec 05 §4 — 사유코드가 계약, 메시지는 front 리소스.
const EDIT_ERROR_MESSAGES: Record<RejectReason, string> = {
  "unit-suffix":
    "단위 표기는 입력할 수 없습니다. 단위를 반영한 숫자로 입력해 주세요. (예: 1,200천원 → 1,200,000)",
  "not-numeric": "숫자 형식만 입력할 수 있습니다.",
  "bad-percent": "비율은 숫자 또는 % 형식으로 입력할 수 있습니다.",
  "bad-date": "올바른 날짜 형식이 아니거나 실제 가능한 날짜가 아닙니다.",
  "bad-duration":
    "기간은 '9년 2개월 3일' 형식으로 입력하거나 숫자만 입력해 연 단위로 입력할 수 있습니다.",
  "bad-boolean": "불리언 값은 true 또는 false만 입력할 수 있습니다.",
  "type-mismatch": "값 형식이 올바르지 않습니다.",
  "integer-required": "정수만 입력할 수 있습니다.",
  "negative-not-allowed": "음수는 입력할 수 없습니다.",
};

/**
 * 저장값 검증은 "사용자가 방금 친 값"이 아니라 "이미 저장된 값"을 보는 경로라 문구가 다르다.
 *
 * 형식·파싱 실패는 전부 "형식이 올바르지 않습니다"로 모은다 — 저장 실패 토스트에
 * "1,200천원 → 1,200,000" 같은 입력 안내가 뜨면 사용자가 할 수 있는 일과 어긋난다.
 * 제약 위반(integer-required·negative-not-allowed)은 사용자가 고칠 수 있는 값이라
 * 편집용 문구를 그대로 쓴다.
 */
const STORED_ERROR_MESSAGES: Partial<Record<RejectReason, string>> = {
  "not-numeric": "숫자 형식이 올바르지 않습니다.",
  "unit-suffix": "숫자 형식이 올바르지 않습니다.",
  "bad-percent": "비율 형식이 올바르지 않습니다.",
  "bad-duration": "기간은 내부적으로 PnYnMnD 형식으로 저장되어야 합니다.",
  "bad-boolean": "불리언 값은 true 또는 false만 저장할 수 있습니다.",
};

const editErrorMessage = (reason: RejectReason, kind: ValueKind): string =>
  reason === "not-numeric" && kind === "MONETARY"
    ? "숫자만 입력할 수 있습니다."
    : EDIT_ERROR_MESSAGES[reason];

/**
 * negated 항목의 nonNegative 거절 문구. 사유코드는 같은 negative-not-allowed지만
 * 사용자가 친 값은 양수다 — 반전 후 저장 부호가 음수라 거절된 것이라, "음수는 입력할 수
 * 없습니다"를 그대로 보여주면 양수를 넣은 사용자는 무엇을 고쳐야 하는지 알 수 없다
 * (convention-runtime-stability "맥락 안내"). 이 경로는 negated 반전을 NUMERIC까지
 * 확장하면서 생겼고, negated ∧ nonNegative에서만 나타난다.
 *
 * 사유코드를 새로 두지 않는 이유: 사유코드는 back과 공유하는 계약(spec 05 §4)이고 문구
 * 분기는 front 리소스라 어댑터 안에서 닫는 편이 영향 범위가 좁다. editErrorMessage에
 * 넣지 않고 발생 지점(normalizeEditedCellValue의 toStored 실패)에서 인라인 분기하는
 * 이유: 반전은 그 경로에서만 적용되므로 negated 컨텍스트가 의미 있는 곳이 거기뿐이다.
 * negated 문구 분기가 하나 더 생기면 그때 editErrorMessage로 모은다.
 */
const NEGATED_NEGATIVE_NOT_ALLOWED_MESSAGE =
  "이 항목은 부호가 반대로 저장되는 항목이라 입력한 값이 음수로 저장됩니다. 부호를 바꿔 입력해 주세요.";

interface EditorPresentation {
  /** 숫자로 읽히는 값 — 셀 우측 정렬 */
  alignRight: boolean;
  inputMode: "text" | "decimal" | "numeric";
  /**
   * 입력창을 "숫자 N자리"로 제한한다(비숫자 실시간 제거 + maxLength).
   * undefined면 자유 입력. 현재 DATE(8자리)만 해당.
   */
  digitsOnlyLength?: number;
}

/**
 * kind별 편집기 표시 결정 — 정렬·입력 모드·입력창 제약을 한 표에 모은다.
 *
 * `Record<ValueKind, …>`인 게 요점이다. ValueKind에 kind가 추가되면 여기서
 * 컴파일이 깨지고(TS2741), 에러가 "이 kind의 정렬·입력 모드를 정해야 한다"는
 * 결정 목록을 그대로 보여준다. 집합(Set)으로 두면 새 kind가 조용히 비숫자로
 * 떨어져 좌측 정렬·text 입력이 되고 아무것도 알려주지 않는다.
 */
const EDITOR_PRESENTATION: Record<ValueKind, EditorPresentation> = {
  MONETARY: { alignRight: true, inputMode: "decimal" },
  NUMERIC: { alignRight: true, inputMode: "decimal" },
  PERCENT: { alignRight: true, inputMode: "decimal" },
  DATE: { alignRight: false, inputMode: "numeric", digitsOnlyLength: 8 },
  DURATION: { alignRight: false, inputMode: "text" },
  BOOLEAN: { alignRight: false, inputMode: "text" },
  TEXT: { alignRight: false, inputMode: "text" },
};

const presentationOf = (schemaType?: string | null): EditorPresentation =>
  EDITOR_PRESENTATION[getIxdSchemaValueKind(schemaType)];

export const sanitizeEditableInputForSchema = (
  schemaType: string | null | undefined,
  value: string,
): string => {
  const length = presentationOf(schemaType).digitsOnlyLength;
  if (length !== undefined) {
    return value.replace(/\D/g, "").slice(0, length);
  }

  // monetary 입력창의 콤마는 포매팅 전용이다 — 사용자가 넣거나 지울 수 없게 매 입력마다
  // 제거 후 재삽입한다. 숫자 형태가 아닌 입력은 insertThousandSeparators가 원문으로
  // 통과시키고, 확정 시 normalizeEditedCellValue가 거절한다.
  if (getIxdSchemaValueKind(schemaType) === "MONETARY") {
    return insertThousandSeparators(value.replace(/,/g, ""));
  }

  return value;
};

/**
 * input/textarea onChange용 sanitize — 값과 함께 커서 위치를 보정한다.
 *
 * monetary 콤마 재삽입은 값 길이를 바꾸므로, 그대로 두면 제어 입력이 재렌더될 때
 * 커서가 끝으로 튀어 값 중간을 고칠 수 없다. DOM value를 먼저 맞춰 두면 React가
 * value를 다시 쓰지 않아(같은 문자열) 여기서 둔 커서가 유지된다. 콤마를 제외한
 * 자릿수를 기준으로 세므로 콤마가 앞에 하나 늘어도 같은 숫자 뒤에 머문다.
 * monetary 외 kind는 기존 동작(보정 없음) 그대로다.
 */
export const sanitizeEditableInputElement = (
  schemaType: string | null | undefined,
  element: HTMLInputElement | HTMLTextAreaElement,
): string => {
  const { value, selectionStart } = element;
  const sanitized = sanitizeEditableInputForSchema(schemaType, value);
  if (sanitized === value || getIxdSchemaValueKind(schemaType) !== "MONETARY") {
    return sanitized;
  }

  const digitsBeforeCaret = value
    .slice(0, selectionStart ?? value.length)
    .replace(/,/g, "").length;
  let caret = 0;
  let seen = 0;
  while (caret < sanitized.length && seen < digitsBeforeCaret) {
    if (sanitized[caret] !== ",") seen += 1;
    caret += 1;
  }

  element.value = sanitized;
  element.setSelectionRange(caret, caret);
  return sanitized;
};

/**
 * keydown 이벤트 중 이 헬퍼가 읽는 것만. React.KeyboardEvent가 구조적으로 들어맞으므로
 * 이 util이 react에 의존하지 않아도 호출부에서 이벤트를 그대로 넘길 수 있다.
 */
interface SeparatorDeleteKeyEvent {
  key: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  currentTarget: HTMLInputElement | HTMLTextAreaElement;
}

/**
 * monetary 입력창 Delete·Backspace 보정 — 커서가 콤마에 맞닿아 있으면 콤마를 건너뛴다.
 *
 * 콤마는 기계 소유라 sanitize가 매 입력마다 같은 자리에 되살린다. 그래서 보정이 없으면
 * 브라우저가 콤마만 지운 결과가 입력 전과 같아지고, 두 삭제 키가 서로 다르게 헛돈다:
 * - Delete(커서가 콤마 **앞**): 커서까지 제자리로 돌아와 그 뒤 숫자를 **영구히** 못 지운다.
 * - Backspace(커서가 콤마 **뒤**): 커서만 콤마 앞으로 밀려 첫 타가 아무 일도 안 한 것처럼
 *   보이고, 숫자는 두 번째 타에야 지워진다.
 * 커서를 콤마 반대편으로 미리 옮겨 두면 두 키 모두 한 번에 숫자를 지운다 — 사용자에게
 * 콤마는 없는 문자처럼 동작한다.
 *
 * 브라우저가 만드는 최종 상태(값·커서)가 두 키에서 같아 onChange로는 구분할 수 없으므로
 * keydown에서 처리한다. 보정하지 않는 경우:
 * - 선택 영역이 있으면 교체 삭제라 콤마만 지워지는 상황이 아니다.
 * - Alt·Ctrl·Meta 조합(단어·행 단위 삭제)은 지우는 범위가 한 글자가 아니므로 커서를 밀면
 *   삭제 범위가 같이 틀어진다. Shift는 수정자로 보지 않는다 — 커서가 붙어 있을 때
 *   단일 문자 삭제와 같게 동작한다.
 */
export const skipSeparatorOnDelete = (
  schemaType: string | null | undefined,
  event: SeparatorDeleteKeyEvent,
): void => {
  const isForward = event.key === "Delete";
  if (!isForward && event.key !== "Backspace") return;
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  if (getIxdSchemaValueKind(schemaType) !== "MONETARY") return;

  const element = event.currentTarget;
  const { selectionStart, selectionEnd } = element;
  if (selectionStart === null || selectionStart !== selectionEnd) return;

  // Delete는 커서 뒤 문자를, Backspace는 커서 앞 문자를 지운다
  const doomed = isForward ? selectionStart : selectionStart - 1;
  if (element.value[doomed] !== ",") return;

  const caret = isForward ? selectionStart + 1 : selectionStart - 1;
  element.setSelectionRange(caret, caret);
};

/** 숫자로 취급하는 schema kind — 우측 정렬 판정용 */
export const isNumericSchemaKind = (schemaType?: string | null): boolean =>
  presentationOf(schemaType).alignRight;

export const formatStoredValueForDisplay = (
  schemaType: string | null | undefined,
  storedValue: string,
  negated: boolean,
  /** 행 헤더 decimalDigits — 숫자 kind의 소수 자릿수 고정 표기(반올림·0 패딩) */
  decimalDigits: number | null = null,
): string => {
  // monetary 표시는 단위 컨텍스트까지 필요해 formatCellForDisplay가 소유한다 —
  // 여기로 오는 monetary는 그 경로가 포기한 값이므로 원문을 그대로 보여준다.
  if (getIxdSchemaValueKind(schemaType) === "MONETARY") return storedValue;

  // negated 반전·dd 자릿수는 storedToView가 숫자 kind 전체에 적용하고 그 외 kind는 무시한다.
  return storedToView(trustedStoredValue(storedValue), {
    kind: getIxdSchemaValueKind(schemaType),
    subKind: schemaType?.trim(),
    negated,
    decimalDigits,
  }).text;
};

/**
 * 편집 진입 초기값. 숫자 kind는 저장 canonical(콤마·trailing zero 없음)을 기본으로
 * 하되, decimalDigits가 있으면 표시와 같은 자릿수로 반올림·0 패딩한다 — 저장값은
 * trailing zero를 갖지 못하므로 "0.0400"으로 보이는 셀을 열자마자 "0.04"로 줄어드는
 * 것을 막는다(monetary의 formatMonetaryTextForEditing과 같은 규칙). negated 반전은
 * 편집값에 적용하지 않는다(편집값은 저장 부호 그대로).
 *
 * 의도된 정밀도 손실: 저장 자릿수가 decimalDigits보다 많으면(원본 IXD에서만 올 수
 * 있다 — 편집을 거친 값은 저장 절삭으로 항상 dd 이내) 초기값은 반올림된 값이고,
 * 사용자가 그 값을 고쳐 확정하면 초과 자릿수는 저장 절삭으로 사라진다. 표시와 편집창을
 * 같은 값으로 두는 쪽을 택한 결과이며 monetary와 동일하다. 변경 없이 확정하면
 * 호출부(editable-table-cell)가 초기값 비교로 저장을 막아 원본이 유지된다.
 */
export const formatStoredValueForEditing = (
  schemaType: string | null | undefined,
  storedValue: string,
  decimalDigits: number | null = null,
): string => {
  const kind = getIxdSchemaValueKind(schemaType);
  switch (kind) {
    case "NUMERIC":
    case "PERCENT": {
      const validation = validateStoredCellValue({
        schemaType,
        storedValue,
      });
      if (!validation.ok) return storedValue;
      if (resolveDecimalDigits(decimalDigits) === null) {
        return validation.normalizedValue;
      }

      return displayTextToEditingText(
        storedToView(trustedStoredValue(validation.normalizedValue), {
          kind,
          subKind: schemaType?.trim(),
          decimalDigits,
        }).text,
      );
    }
    case "DATE": {
      // 입력창은 숫자만 받는다(sanitize가 구분자를 실시간 제거) — value-spec 06 §3
      const dashed = storedToView(trustedStoredValue(storedValue), {
        kind: "DATE",
      }).text;
      return dashed.replace(/\D/g, "").slice(0, 8);
    }
    case "DURATION":
      return storedToView(trustedStoredValue(storedValue), {
        kind: "DURATION",
      }).text;
    default:
      return storedValue;
  }
};

export const getEditableInputModeForSchema = (
  schemaType: string | null | undefined,
): "text" | "decimal" | "numeric" => presentationOf(schemaType).inputMode;

export const getEditableMaxLengthForSchema = (
  schemaType: string | null | undefined,
): number | undefined => presentationOf(schemaType).digitsOnlyLength;

/**
 * 편집 입력 → 저장값. `toView`(자유 입력 파싱) → `toStored`(dd 버림·반전·단위 환원).
 *
 * toView에 scale=0을 주는 이유: 편집기 입력은 이미 단위가 적용된 표시값이라
 * ×10^scale 환원은 저장 단계에서 한 번만 일어나야 한다(이중 환산 방지).
 */
export const normalizeEditedCellValue = ({
  schemaType,
  inputValue,
  unitDivisor = 1,
  displayValueMode = "raw",
  displayUsesNegatedSign = false,
  negated = false,
  decimalDigits = null,
}: NormalizeEditedCellValueOptions): EditedValueValidationResult => {
  const kind = getIxdSchemaValueKind(schemaType);
  const base = {
    kind,
    // kind를 trim한 값으로 정했으므로 subKind도 같은 문자열을 봐야 한다 — 안 그러면
    // " intItemType "에서 kind만 numeric이고 정수 제약이 조용히 빠진다.
    subKind: schemaType?.trim(),
    negated: displayUsesNegatedSign && negated,
    decimalDigits,
  };

  const view = toView(rawValue(inputValue), { ...base, scale: 0 });
  if (!view.ok) {
    return { ok: false, errorMessage: editErrorMessage(view.reason, kind) };
  }

  // scale이 저장값에 반영되는 건 MONETARY뿐이고(to-stored.ts), 빈 값은 어느 kind에서도
  // 환산 대상이 아니다. 호출부는 schemaType과 무관하게 unit-applied를 넘기므로
  // 여기서 좁히지 않으면 divisor가 깨졌을 때 단위와 무관한 date·text 셀 편집과
  // 셀 비우기까지 "단위 정보가 올바르지 않습니다"로 막힌다.
  const needsScale =
    displayValueMode === "unit-applied" &&
    kind === "MONETARY" &&
    view.value.text !== "";
  const scale = needsScale ? toUnitScale(unitDivisor) : 0;

  if (scale === null) {
    return {
      ok: false,
      errorMessage:
        "셀의 단위 정보가 올바르지 않습니다. 새로고침 후 다시 시도해 주세요.",
    };
  }

  const stored = toStored(view.value, { ...base, scale });
  if (stored.ok) return { ok: true, storedValue: stored.value.text };
  return {
    ok: false,
    errorMessage:
      stored.reason === "negative-not-allowed" && base.negated
        ? NEGATED_NEGATIVE_NOT_ALLOWED_MESSAGE
        : editErrorMessage(stored.reason, kind),
  };
};

/**
 * 저장값 검증/정규화 — 단위·반전이 없는 중립 컨텍스트의 `toStored`.
 *
 * 입력이 이미 저장 단계 문법(예: DURATION "P14M35D")이라 toView를 거치지 않는다.
 * 여기서 나온 값은 normalizeEditedCellValue 결과와 반드시 같아야 한다(멱등).
 *
 * 앞뒤 정리를 여기서 하는 이유: `toStored`의 계약은 "표시 정규형 → 저장 canonical"이고
 * 앞뒤 문자 정리는 toView 몫이다. toView를 건너뛰는 건 이 합성뿐이라(spec에 대응 suite가
 * 없다) 그 빈틈은 공유 모듈이 아니라 어댑터가 메운다. 안 하면 공백만 있는 값이 숫자
 * kind는 통과, DATE·DURATION·BOOLEAN은 거절로 갈린다. TEXT는 원문 보존이 계약이라 제외.
 */
export const validateStoredCellValue = ({
  schemaType,
  storedValue,
}: ValidateStoredCellValueOptions): StoredValueValidationResult => {
  const kind = getIxdSchemaValueKind(schemaType);
  const view = kind === "TEXT" ? storedValue : stripInvisibleEdges(storedValue);
  const result = toStored(viewValue(view), {
    kind,
    // kind는 trim한 값으로 정하므로 subKind도 같은 문자열을 봐야 한다 —
    // 안 그러면 " intItemType "에서 kind만 numeric이고 정수 제약이 조용히 빠진다.
    subKind: schemaType?.trim(),
  });

  if (result.ok) {
    return { ok: true, normalizedValue: result.value.text };
  }

  return {
    ok: false,
    errorMessage:
      STORED_ERROR_MESSAGES[result.reason] ??
      editErrorMessage(result.reason, kind),
  };
};
